/**
 * Iter 68g — Live Admin Chat Console.
 *
 * Floating bottom-left popup mounted globally inside the AdminDashboard.
 * Polls /admin/chat-leads/active every 5s; when a new active session
 * appears (or a new customer message lands in an existing session),
 * the popup auto-opens with a chime + the conversation view.
 *
 * Behaviour:
 *   • Auto-open on new activity (respects per-session "dismissed"
 *     state so a closed conversation doesn't keep re-opening).
 *   • Admin can type a reply → POST /admin/chat-leads/{id}/reply →
 *     flips `human_taken_over` so the AI bot stops auto-replying.
 *   • "Hand back to AI" button calls /release.
 *   • Multi-admin: last admin to reply "owns" the session, but every
 *     admin sees the live transcript and can chime in.
 *   • Customer's chat bubble polls the new /chat/messages endpoint
 *     and renders admin messages with a "Support — {name}" badge.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import useDraggableFab from "@/lib/useDraggableFab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  MessageCircle, X, Send, Loader2, ChevronLeft, Phone, Mail,
  RotateCcw, Volume2, VolumeX, Sparkles, ShieldCheck, ExternalLink,
} from "lucide-react";

const POLL_MS = 5_000;
const MUTE_KEY = "rentalcar:admin_live_chat_mute";
const DISMISSED_KEY_PREFIX = "rentalcar:admin_live_chat_dismissed:";

function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tone = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(0.2, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    };
    // Slightly more attention-grabbing than the new-lead chime — three
    // ascending notes, ~0.8s total.
    tone(659.25, 0, 0.18);  // E5
    tone(880, 0.16, 0.18);  // A5
    tone(1108.7, 0.34, 0.30); // C#6
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 1000);
  } catch { /* swallow */ }
}

export default function AdminLiveChatPopup() {
  const [open, setOpen] = useState(false);
  const fab = useDraggableFab("rentalcar:livechat-fab", "left");
  const [activeSessions, setActiveSessions] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; }
  });
  const seenActivity = useRef(new Map()); // lead_id -> last_message_at
  const transcriptRef = useRef(null);

  // ───── Active sessions poll ────────────────────────────────────
  const refreshActive = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/chat-leads/active?minutes=60&limit=50");
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      // Diff for chime + auto-open. Only fires once per (session, ts)
      // pair so a refresh of the dashboard doesn't re-chime old activity.
      let triggered = null;
      for (const r of rows) {
        const prev = seenActivity.current.get(r.lead_id);
        const curr = r.last_message_at;
        if (curr && curr !== prev) {
          // First sighting = seed only, no chime.
          const dismissed = (() => {
            try { return sessionStorage.getItem(DISMISSED_KEY_PREFIX + r.lead_id) === curr; } catch { return false; }
          })();
          if (prev !== undefined && !dismissed) {
            triggered = r;
          }
          seenActivity.current.set(r.lead_id, curr);
        }
      }
      setActiveSessions(rows);
      if (triggered) {
        if (!muted) playChime();
        toast.success(
          `Chat activity · ${triggered.name}: "${(triggered.last_message_preview || "(typing…)").slice(0, 60)}"`,
          { duration: 7000 }
        );
        setOpen(true);
        if (!selectedLeadId) setSelectedLeadId(triggered.lead_id);
      }
    } catch {
      /* polling errors are silent — toast spam would be worse */
    }
  }, [muted, selectedLeadId]);

  useEffect(() => {
    refreshActive();
    const id = setInterval(refreshActive, POLL_MS);
    return () => clearInterval(id);
  }, [refreshActive]);

  // ───── Conversation poll for the selected session ──────────────
  const refreshConversation = useCallback(async () => {
    if (!selectedLeadId) return;
    try {
      const { data } = await api.get(`/admin/chat-leads/${selectedLeadId}/conversation`);
      setConversation(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [selectedLeadId]);

  useEffect(() => {
    if (!selectedLeadId) { setConversation(null); return; }
    refreshConversation();
    const id = setInterval(refreshConversation, POLL_MS);
    return () => clearInterval(id);
  }, [selectedLeadId, refreshConversation]);

  // Scroll to bottom on new messages.
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [conversation]);

  const sendReply = async (e) => {
    e?.preventDefault?.();
    const text = replyText.trim();
    if (!text || !selectedLeadId || sending) return;
    setSending(true);
    try {
      await api.post(`/admin/chat-leads/${selectedLeadId}/reply`, { message: text });
      setReplyText("");
      await refreshConversation();
      toast.success("Reply sent");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const releaseToBot = async () => {
    if (!selectedLeadId || releasing) return;
    if (!window.confirm("Hand this conversation back to the AI assistant? You can take over again any time.")) return;
    setReleasing(true);
    try {
      await api.post(`/admin/chat-leads/${selectedLeadId}/release`, {});
      await refreshConversation();
      toast.success("Conversation handed back to AI");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReleasing(false);
    }
  };

  const dismissPopup = () => {
    // Remember the last-seen activity ts so we don't re-pop until a new
    // message lands.
    if (selectedLeadId) {
      const lastSeen = seenActivity.current.get(selectedLeadId);
      try { sessionStorage.setItem(DISMISSED_KEY_PREFIX + selectedLeadId, lastSeen || ""); } catch { /* ignore */ }
    }
    setOpen(false);
    setSelectedLeadId(null);
  };

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      try { localStorage.setItem(MUTE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const newActivityCount = useMemo(() => {
    return activeSessions.filter((r) => {
      try {
        const dismissed = sessionStorage.getItem(DISMISSED_KEY_PREFIX + r.lead_id);
        return dismissed !== r.last_message_at && r.message_count > 0;
      } catch { return false; }
    }).length;
  }, [activeSessions]);

  const selected = useMemo(
    () => activeSessions.find((r) => r.lead_id === selectedLeadId) || null,
    [activeSessions, selectedLeadId]
  );

  return (
    <>
      {/* Trigger button — draggable FAB (Iter 110): auto-positions above the
          mobile bottom nav + iOS safe area, drag to move, snaps to the
          nearest edge, tap still opens. */}
      {!open && (
        <button
          type="button"
          style={fab.style}
          onPointerDown={fab.onPointerDown}
          onClick={() => { if (!fab.wasDragged()) setOpen(true); }}
          className="z-50 h-14 px-4 rounded-full bg-neutral-900 hover:bg-neutral-800 text-amber-400 shadow-2xl ring-1 ring-amber-500/30 flex items-center gap-2 transition-colors select-none"
          aria-label="Open live chat console (drag to move)"
          data-testid="admin-live-chat-open"
        >
          <MessageCircle className="h-5 w-5" />
          <span className="text-sm font-semibold">Live Chat</span>
          {newActivityCount > 0 && (
            <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-amber-400 text-neutral-900 text-[10px] font-bold flex items-center justify-center animate-pulse">
              {newActivityCount}
            </span>
          )}
        </button>
      )}

      {open && (
        <div
          className="fixed z-50 left-2 right-2 bottom-[calc(env(safe-area-inset-bottom)+4.75rem)] lg:left-5 lg:right-auto lg:bottom-5 lg:w-[min(420px,calc(100vw-2.5rem))] flex flex-col h-[min(620px,calc(100dvh-9.5rem))] lg:h-[min(620px,calc(100vh-2.5rem))] rounded-3xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden"
          role="dialog"
          aria-label="Admin live chat console"
          data-testid="admin-live-chat-panel"
        >
          {/* Header */}
          <div className="bg-neutral-900 text-amber-400 px-4 py-3 flex items-center gap-2">
            {selectedLeadId && (
              <button
                type="button"
                onClick={() => setSelectedLeadId(null)}
                className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center"
                aria-label="Back to session list"
                data-testid="admin-live-chat-back"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <div className="h-8 w-8 rounded-full bg-amber-500 text-neutral-900 flex items-center justify-center shrink-0">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-sm font-semibold">
                {selected ? selected.name : "Live Chat Console"}
              </div>
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">
                {selected
                  ? selected.human_taken_over
                    ? `You're chatting · ${selected.human_owner_name || "Admin"}`
                    : "AI handling · click reply to take over"
                  : `${activeSessions.length} active session${activeSessions.length === 1 ? "" : "s"}`}
              </div>
            </div>
            <button
              type="button"
              onClick={toggleMute}
              className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center"
              aria-label={muted ? "Unmute chime" : "Mute chime"}
              title={muted ? "Sound muted — click to enable" : "Sound on — click to mute"}
              data-testid="admin-live-chat-mute"
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={dismissPopup}
              className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center"
              aria-label="Close console"
              data-testid="admin-live-chat-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {!selectedLeadId ? (
            // ─────── Session list ─────────────────────────────
            <div className="flex-1 overflow-y-auto bg-neutral-50/60" data-testid="admin-live-chat-list">
              {activeSessions.length === 0 ? (
                <div className="text-center py-12 px-6 text-neutral-500 text-sm">
                  <MessageCircle className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
                  No active chat sessions in the last 60 minutes.
                </div>
              ) : (
                <ul className="divide-y divide-neutral-100">
                  {activeSessions.map((r) => {
                    const isNew = (() => {
                      try {
                        return sessionStorage.getItem(DISMISSED_KEY_PREFIX + r.lead_id) !== r.last_message_at
                          && r.message_count > 0;
                      } catch { return false; }
                    })();
                    return (
                      <li
                        key={r.lead_id}
                        className={`px-4 py-3 hover:bg-amber-50/60 cursor-pointer transition ${isNew ? "bg-amber-50" : ""}`}
                        onClick={() => setSelectedLeadId(r.lead_id)}
                        data-testid={`admin-live-chat-row-${r.lead_id}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-sm text-neutral-900 truncate flex-1">
                            {r.name}
                          </div>
                          {isNew && (
                            <span className="text-[9px] uppercase tracking-wider font-bold text-amber-800 bg-amber-200 ring-1 ring-amber-300 px-1.5 py-0.5 rounded-full">
                              NEW
                            </span>
                          )}
                          {r.human_taken_over && (
                            <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 ring-1 ring-emerald-200 px-1.5 py-0.5 rounded-full">
                              {r.human_owner_name === r.human_owner_name ? r.human_owner_name : "Human"}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-neutral-500 flex items-center gap-2 mt-0.5">
                          <Mail className="h-3 w-3" /> {r.email}
                          <Phone className="h-3 w-3 ml-1" /> {r.phone}
                        </div>
                        <div className="text-[12px] text-neutral-700 mt-1 line-clamp-2">
                          {r.last_message_preview || <span className="italic text-neutral-400">No messages yet — visitor just opened the chat.</span>}
                        </div>
                        <div className="text-[10px] text-neutral-400 mt-1 flex items-center gap-2">
                          <span>{r.message_count} msg{r.message_count === 1 ? "" : "s"}</span>
                          {r.source_path && (
                            <span className="truncate font-mono">· {r.source_path}</span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : (
            // ─────── Conversation view ───────────────────────
            <>
              {/* Lead contact strip */}
              {selected && (
                <div className="px-4 py-2 bg-neutral-50 border-b border-neutral-100 text-[11px] text-neutral-600 flex items-center gap-3 flex-wrap">
                  <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                    <Mail className="h-3 w-3" /> {selected.email}
                  </a>
                  <a href={`https://wa.me/${(selected.phone || "").replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-emerald-700 hover:underline">
                    <Phone className="h-3 w-3" /> {selected.phone}
                  </a>
                  {selected.source_path && (
                    <a href={selected.source_path} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-neutral-500 hover:text-blue-600 font-mono">
                      {selected.source_path} <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  )}
                </div>
              )}

              {/* Transcript */}
              <div
                ref={transcriptRef}
                className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-neutral-50/40"
                data-testid="admin-live-chat-transcript"
              >
                {!conversation ? (
                  <div className="text-center py-8 text-neutral-400 text-sm">
                    <Loader2 className="h-4 w-4 mx-auto animate-spin" />
                  </div>
                ) : (conversation.messages || []).length === 0 ? (
                  <div className="text-center py-8 text-neutral-500 text-sm italic">
                    Visitor hasn't sent the first message yet. You can start the conversation with a greeting.
                  </div>
                ) : (
                  (conversation.messages || []).map((m, i) => (
                    <div
                      key={i}
                      className={`max-w-[88%] ${m.role === "user" ? "mr-auto" : "ml-auto"}`}
                    >
                      <div
                        className={`px-3 py-2 rounded-2xl text-[13px] leading-relaxed ${
                          m.role === "user"
                            ? "bg-white text-neutral-900 border border-neutral-100 rounded-bl-md"
                            : m.role === "admin"
                              ? "bg-amber-500 text-amber-950 rounded-br-md"
                              : m.role === "system"
                                ? "bg-neutral-100 text-neutral-500 text-[11px] italic"
                                : "bg-blue-50 text-blue-900 rounded-br-md"
                        }`}
                      >
                        {m.content}
                      </div>
                      <div className={`text-[9px] text-neutral-400 mt-0.5 px-2 ${m.role === "user" ? "" : "text-right"}`}>
                        {m.role === "admin" && m.admin_name ? `${m.admin_name} · ` : ""}
                        {m.role === "user" ? selected?.name?.split(" ")[0] || "Visitor" : m.role === "assistant" ? "AI" : ""}
                        {m.at && ` · ${new Date(m.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Composer */}
              <div className="border-t border-neutral-200 bg-white p-3">
                {conversation?.human_taken_over && conversation?.human_owner_name && (
                  <div className="flex items-center justify-between text-[10px] text-emerald-700 mb-2">
                    <span className="inline-flex items-center gap-1">
                      <ShieldCheck className="h-3 w-3" /> Human in control · {conversation.human_owner_name}
                    </span>
                    <button
                      type="button"
                      onClick={releaseToBot}
                      disabled={releasing}
                      className="text-[10px] text-neutral-500 hover:text-neutral-800 inline-flex items-center gap-1"
                      data-testid="admin-live-chat-release"
                    >
                      {releasing ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <RotateCcw className="h-2.5 w-2.5" />}
                      Hand back to AI
                    </button>
                  </div>
                )}
                <form onSubmit={sendReply} className="flex items-center gap-2">
                  <Input
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply…"
                    className="rounded-full h-9 text-sm flex-1"
                    disabled={sending}
                    maxLength={2000}
                    data-testid="admin-live-chat-input"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    disabled={!replyText.trim() || sending}
                    className="rounded-full bg-amber-500 hover:bg-amber-400 text-amber-950"
                    data-testid="admin-live-chat-send"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </form>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
